import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { resolveImg } from "../lib/img";

// Custom divIcon factory per category
const makeIcon = (variant) => L.divIcon({
    className: "",
    html: `<div class="cp-marker cp-marker--${variant}"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
});

const ICONS = {
    places: makeIcon("place"),
    events: makeIcon("event"),
    walks: makeIcon("walk"),
    experiences: makeIcon("experience"),
    lodgings: makeIcon("lodging"),
};

const LAYERS = [
    { key: "places", label: "Adresses", color: "#6a7d77" },
    { key: "lodgings", label: "Logements", color: "#c9b896" },
    { key: "experiences", label: "Expériences", color: "#a0522d" },
    { key: "events", label: "Événements", color: "#2d4a3c" },
    { key: "walks", label: "Balades", color: "#7c8a84" },
];

export default function Carte() {
    const [data, setData] = useState({ places: [], events: [], walks: [], experiences: [], lodgings: [] });
    const [active, setActive] = useState({ places: true, events: true, walks: true, experiences: true, lodgings: true });
    const [loading, setLoading] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get("/map-items", { params: { types: "places,events,walks,experiences,lodgings" } })
            .then((r) => setData({
                places: r.data.places || [],
                events: r.data.events || [],
                walks: r.data.walks || [],
                experiences: r.data.experiences || [],
                lodgings: r.data.lodgings || [],
            }))
            .finally(() => setLoading(false));
    }, []);

    const center = useMemo(() => [50.055, 1.4], []);

    const counts = {
        places: data.places.length,
        lodgings: data.lodgings.length,
        experiences: data.experiences.length,
        events: data.events.length,
        walks: data.walks.length,
    };

    const total = Object.values(counts).reduce((a, b) => a + b, 0);

    return (
        <>
            <SEO
            title="Carte interactive — 380+ points sur la côte chic"
            description="Carte interactive de tout le territoire Côte Perdue : 289 adresses, 53 hôtels, 42 expériences, 11 randonnées entre Le Tréport, Mers-les-Bains, Cayeux-sur-Mer, Eu, Aumale."
            path="/carte"
        />
            <div className="pt-24" data-testid="map-page">
            <section className="px-6 md:px-12 max-w-[1600px] mx-auto pt-10 md:pt-14 pb-6">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <p className="cp-tiny text-cp-foret mb-4">Carte interactive · {total} points</p>
                        <h1 className="cp-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-cp-encre max-w-3xl">
                            Une côte, <em className="cp-display-italic text-cp-foret">cent secrets</em>.
                        </h1>
                        <p className="cp-serif italic text-base md:text-lg text-cp-gris mt-4 max-w-2xl">
                            Tous les points du territoire, filtrables par couche. Zoomez, cliquez, explorez.
                        </p>
                    </div>
                </div>

                {/* Layer toggles */}
                <div className="mt-8" data-testid="map-filters">
                    <button
                        type="button"
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        className="md:hidden w-full flex items-center justify-between py-3 px-4 border border-cp-border bg-cp-ivoire"
                        data-testid="map-filter-toggle"
                    >
                        <span className="cp-kicker text-[11px] text-cp-encre">
                            Couches · <span className="text-cp-sable-deep">{Object.values(active).filter(Boolean).length} actives</span>
                        </span>
                        <span className={`transition-transform text-cp-foret cp-serif italic text-lg ${filtersOpen ? "rotate-90" : ""}`}>→</span>
                    </button>
                    <div className={`${filtersOpen ? "flex" : "hidden"} md:flex flex-wrap items-center gap-2 mt-3 md:mt-0`}>
                        {LAYERS.map((l) => (
                            <button
                                key={l.key}
                                onClick={() => setActive((a) => ({ ...a, [l.key]: !a[l.key] }))}
                                data-testid={`map-layer-${l.key}`}
                                className={`flex items-center gap-2 cp-tiny px-4 py-2.5 border transition-colors ${
                                    active[l.key] ? "border-cp-foret bg-cp-foret text-cp-ivoire" : "border-cp-border text-cp-encre hover:border-cp-foret"
                                }`}
                            >
                                <span className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                                {l.label}
                                <span className={`text-[9px] opacity-70 ${active[l.key] ? "text-cp-ivoire" : "text-cp-gris"}`}>({counts[l.key]})</span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[1600px] mx-auto pb-24">
                <div className="border border-cp-border overflow-hidden" style={{ height: "75vh" }}>
                    {loading ? (
                        <div className="h-full flex items-center justify-center text-cp-gris cp-serif italic">Chargement de la carte…</div>
                    ) : (
                        <MapContainer center={center} zoom={10} scrollWheelZoom className="h-full w-full">
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · Côte Perdue'
                                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                            />

                            {active.places && data.places.map((p) => p.lat && p.lng && (
                                <Marker key={`p-${p.id}`} position={[p.lat, p.lng]} icon={ICONS.places}>
                                    <Popup>
                                        <div className="w-64" style={{ fontFamily: "Manrope" }}>
                                            {p.image && <img src={resolveImg(p.image)} alt={p.name} className="w-full h-32 object-cover" />}
                                            <div className="p-3">
                                                <p className="cp-tiny text-cp-gris mb-1">{p.category}</p>
                                                <p className="cp-serif text-lg leading-tight mb-1 text-cp-encre">{p.name}</p>
                                                <p className="text-xs text-cp-gris line-clamp-2">{p.short_description}</p>
                                                <Link to={`/adresses/${p.slug}`} className="cp-tiny text-cp-foret mt-2 inline-block">Découvrir →</Link>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}

                            {active.lodgings && data.lodgings.map((l) => (
                                <Marker key={`l-${l.id}`} position={[l.lat, l.lng]} icon={ICONS.lodgings}>
                                    <Popup>
                                        <div className="w-64" style={{ fontFamily: "Manrope" }}>
                                            <img src={resolveImg(l.image)} alt={l.name} className="w-full h-32 object-cover" />
                                            <div className="p-3">
                                                <p className="cp-tiny text-cp-terre mb-1">Logement · {l.city}</p>
                                                <p className="cp-serif text-lg leading-tight mb-1 text-cp-encre">{l.name}</p>
                                                <p className="text-xs text-cp-gris line-clamp-2">{l.pitch}</p>
                                                <p className="cp-tiny text-cp-foret mt-2">dès {l.price_from}€</p>
                                                <Link to="/loger" className="cp-tiny text-cp-foret mt-1 inline-block">Voir tous les logements →</Link>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}

                            {active.experiences && data.experiences.map((x) => (
                                <Marker key={`x-${x.id}`} position={[x.lat, x.lng]} icon={ICONS.experiences}>
                                    <Popup>
                                        <div className="w-64" style={{ fontFamily: "Manrope" }}>
                                            {x.image && <img src={resolveImg(x.image)} alt={x.title} className="w-full h-32 object-cover" />}
                                            <div className="p-3">
                                                <p className="cp-tiny text-cp-terre mb-1">Expérience · {x.category}</p>
                                                <p className="cp-serif text-lg leading-tight mb-1 text-cp-encre">{x.title}</p>
                                                <p className="text-xs text-cp-gris line-clamp-2">{x.short_description}</p>
                                                {x.price_from && <p className="cp-tiny text-cp-foret mt-2">{x.price_from}</p>}
                                                <Link to={`/experiences/${x.slug}`} className="cp-tiny text-cp-foret mt-1 inline-block">Réserver →</Link>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}

                            {active.events && data.events.map((e) => e.lat && e.lng && (
                                <Marker key={`e-${e.id}`} position={[e.lat, e.lng]} icon={ICONS.events}>
                                    <Popup>
                                        <div className="w-64" style={{ fontFamily: "Manrope" }}>
                                            {e.image && <img src={resolveImg(e.image)} alt={e.title} className="w-full h-32 object-cover" />}
                                            <div className="p-3">
                                                <p className="cp-tiny text-cp-foret mb-1">Événement</p>
                                                <p className="cp-serif text-lg leading-tight mb-1 text-cp-encre">{e.title}</p>
                                                <p className="text-xs text-cp-gris">{new Date(e.start_date).toLocaleDateString("fr-FR")}</p>
                                                <Link to={`/evenements/${e.slug}`} className="cp-tiny text-cp-foret mt-2 inline-block">En savoir plus →</Link>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}

                            {active.walks && data.walks.map((w) => w.start_lat && w.start_lng && (
                                <Marker key={`w-${w.id}`} position={[w.start_lat, w.start_lng]} icon={ICONS.walks}>
                                    <Popup>
                                        <div className="w-64" style={{ fontFamily: "Manrope" }}>
                                            {w.image && <img src={resolveImg(w.image)} alt={w.title} className="w-full h-32 object-cover" />}
                                            <div className="p-3">
                                                <p className="cp-tiny text-cp-gris mb-1">Balade · {w.theme}</p>
                                                <p className="cp-serif text-lg leading-tight mb-1 text-cp-encre">{w.title}</p>
                                                <p className="text-xs text-cp-gris">{w.distance_km} km · {w.duration_minutes} min</p>
                                                <Link to={`/balades/${w.slug}`} className="cp-tiny text-cp-foret mt-2 inline-block">Partir →</Link>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    )}
                </div>
                <p className="cp-tiny text-cp-gris mt-4 flex flex-wrap gap-x-4 gap-y-2">
                    Légende :
                    {LAYERS.map(l => (
                        <span key={l.key} className="inline-flex items-center gap-1.5">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: l.color }} />
                            {l.label}
                        </span>
                    ))}
                </p>
            </section>
        </div>
    </>

    );
}
